import type { ReactNode } from "react"

export type AttendanceColumn<T> = {
  key: string
  label: string
  align?: "left" | "right"
  render: (row: T) => ReactNode
  /** En móvil es el título de la fila. Una sola columna por tabla. */
  primary?: boolean
  /** En móvil va al pie de la fila, a todo el ancho (p. ej. el botón de escanear). */
  action?: boolean
  /** Celda de la fila de totales; vacío si la columna no se suma. */
  footer?: ReactNode
}

type Props<T> = {
  columns: AttendanceColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string
  /** Texto accesible de la tabla (lo lee el lector de pantalla, no se ve). */
  caption: string
  emptyMessage: string
  /** Si se da, se pinta la fila de totales con este rótulo en la primera columna. */
  footerLabel?: string
  onRowClick?: (row: T) => void
}

const numeric = "tabular-nums"

/**
 * Tabla de gestión de CCCD para escritorio y lista abierta en móvil (sin tarjetas): una sola
 * definición de columnas alimenta las dos presentaciones, así no hay marcado duplicado a mano.
 * Los ceros se pintan siempre; una celda vacía en una tabla de conteos se lee como "roto".
 */
export function AttendanceTable<T>({ columns, rows, rowKey, caption, emptyMessage, footerLabel, onRowClick }: Props<T>) {
  const primary = columns.find((c) => c.primary) ?? columns[0]
  const action = columns.find((c) => c.action)
  const details = columns.filter((c) => c !== primary && c !== action)
  const hasFooter = !!footerLabel && rows.length > 0

  if (rows.length === 0) {
    return <p className="border-y border-gray-200 px-4 py-10 text-center text-sm text-gray-500 dark:border-gray-800 dark:text-gray-400">{emptyMessage}</p>
  }

  return (
    <>
      {/* Móvil: lista abierta, filas separadas por líneas. */}
      <ul className="divide-y divide-gray-200 border-y border-gray-200 sm:hidden dark:divide-gray-800 dark:border-gray-800">
        {rows.map((row) => (
          <li key={rowKey(row)} className="py-4">
            <div className="mb-2">{primary.render(row)}</div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {details.map((c) => (
                <div key={c.key} className="flex items-baseline justify-between gap-2">
                  <dt className="text-gray-500 dark:text-gray-400">{c.label}</dt>
                  <dd className={`text-right text-gray-900 dark:text-white ${numeric}`}>{c.render(row)}</dd>
                </div>
              ))}
            </dl>
            {action && <div className="mt-3">{action.render(row)}</div>}
          </li>
        ))}
        {hasFooter && (
          <li className="bg-gray-50 py-4 dark:bg-white/[0.03]">
            <p className="mb-2 font-semibold text-gray-900 dark:text-white">{footerLabel}</p>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {details.filter((c) => c.footer !== undefined).map((c) => (
                <div key={c.key} className="flex items-baseline justify-between gap-2">
                  <dt className="text-gray-500 dark:text-gray-400">{c.label}</dt>
                  <dd className={`text-right font-semibold text-gray-900 dark:text-white ${numeric}`}>{c.footer}</dd>
                </div>
              ))}
            </dl>
          </li>
        )}
      </ul>

      {/* Escritorio y tablet: tabla real, con encabezados con scope para lectores de pantalla. */}
      <div className="hidden overflow-x-auto rounded-2xl border border-gray-200 sm:block dark:border-gray-800">
        <table className="w-full text-sm">
          <caption className="sr-only">{caption}</caption>
          <thead className="bg-gray-50 text-xs uppercase text-gray-500 dark:bg-gray-800/50 dark:text-gray-400">
            <tr>
              {columns.map((c) => (
                <th key={c.key} scope="col" className={`px-4 py-3 font-medium ${c.align === "right" ? "text-right" : "text-left"}`}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={onRowClick ? "cursor-pointer transition hover:bg-gray-50 dark:hover:bg-white/[0.03]" : undefined}
              >
                {columns.map((c) => (
                  <td key={c.key} className={`px-4 py-3 text-gray-700 dark:text-gray-300 ${c.align === "right" ? `text-right ${numeric}` : "text-left"}`}>
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {hasFooter && (
            <tfoot className="border-t border-gray-200 bg-gray-50 font-semibold text-gray-900 dark:border-gray-800 dark:bg-gray-800/50 dark:text-white">
              <tr>
                {columns.map((c, i) => (
                  <td key={c.key} className={`px-4 py-3 ${c.align === "right" ? `text-right ${numeric}` : "text-left"}`}>
                    {i === 0 ? footerLabel : c.footer}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </>
  )
}
